import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { Bell, Package, ShieldAlert, Store, Megaphone, Info, Check, UserPlus } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  getUnreadCount,
  getNotifications,
  markAsRead,
  markAllAsRead,
  publishUnreadCount,
} from "../services/notificationService";
import { useAuth } from "../context/AuthContext";
import { timeAgo } from "../utils/timeAgo";
import { useTimeRefresh } from "../hooks/useTimeRefresh";
import { resolveAccountNotificationUrl } from "../utils/notificationLinks";
import { ListSkeleton } from "./skeletons/ShopperSkeletons";

/* Category → icon + colour */
const CAT_META = {
  ORDER:      { Icon: Package,     color: "text-blue-500",    bg: "bg-blue-50    dark:bg-blue-900/30" },
  SECURITY:   { Icon: ShieldAlert, color: "text-red-500",     bg: "bg-red-50     dark:bg-red-900/30" },
  ACCOUNT:    { Icon: Store,       color: "text-emerald-500", bg: "bg-emerald-50 dark:bg-emerald-900/30" },
  PROMO:      { Icon: Megaphone,   color: "text-pink-500",    bg: "bg-pink-50    dark:bg-pink-900/30" },
  SYSTEM:     { Icon: Info,        color: "text-neutral-500", bg: "bg-neutral-100 dark:bg-neutral-800" },
  INVITATION: { Icon: UserPlus,    color: "text-violet-500",  bg: "bg-violet-50  dark:bg-violet-900/30" },
};

/* timeAgo imported from ../utils/timeAgo */

export default function NotificationBell() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  useTimeRefresh(30_000);
  const [count, setCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);
  const anchorRef = useRef(null);
  const panelRef = useRef(null);
  const mountedRef = useRef(true);
  const [panelPos, setPanelPos] = useState({ top: 0, right: 8 });

  useEffect(() => {
    publishUnreadCount(count);
  }, [count]);

  const updatePanelPosition = useCallback(() => {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const nextTop = Math.max(8, rect.bottom + 8);
    const nextRight = Math.max(8, window.innerWidth - rect.right);
    setPanelPos({ top: nextTop, right: nextRight });
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Close on outside click / ESC
  useEffect(() => {
    const onClick = (e) => {
      const target = e.target;
      const clickedAnchor = anchorRef.current?.contains(target);
      const clickedPanel = panelRef.current?.contains(target);
      if (!clickedAnchor && !clickedPanel) setOpen(false);
    };
    const onEsc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, []);

  useEffect(() => {
    if (!open) return;

    updatePanelPosition();

    const onResize = () => updatePanelPosition();
    const onScroll = () => updatePanelPosition();

    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [open, updatePanelPosition]);

  // Initial unread count
  useEffect(() => {
    if (!isAuthenticated) { setCount(0); setItems([]); return; }
    getUnreadCount().then((c) => mountedRef.current && setCount(c)).catch(() => {});
  }, [isAuthenticated]);

  // Refresh unread count every 30 seconds while visible and on tab return.
  useEffect(() => {
    if (!isAuthenticated) return;

    const refresh = () => {
      getUnreadCount().then((c) => mountedRef.current && setCount(c)).catch(() => {});
    };

    // Refetch whenever the tab becomes visible again
    const onVisibility = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVisibility);

    // Polling fallback in case SSE drops silently
    const interval = setInterval(refresh, 30_000);

    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      clearInterval(interval);
    };
  }, [isAuthenticated]);

  // Fetch recent when dropdown opens
  useEffect(() => {
    if (!open || !isAuthenticated) return;
    setLoading(true);
    getNotifications({ page: 1, limit: 6 })
      .then((res) => {
        if (mountedRef.current) setItems(res.items || []);
      })
      .catch(() => {})
      .finally(() => mountedRef.current && setLoading(false));
  }, [open, isAuthenticated]);

  const handleMarkRead = async (e, id) => {
    e.stopPropagation();
    try {
      await markAsRead(id);
      setItems((prev) => prev.map((n) => (n.id === id ? { ...n, isRead: true } : n)));
      setCount((prev) => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const handleMarkAllRead = async () => {
    setMarkingAll(true);
    try {
      await markAllAsRead();
      setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setCount(0);
    } catch { /* silent */ }
    if (mountedRef.current) setMarkingAll(false);
  };

  const handleItemClick = (n) => {
    if (!n.isRead) {
      markAsRead(n.id).catch(() => {});
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, isRead: true } : x)));
      setCount((prev) => Math.max(0, prev - 1));
    }
    setOpen(false);
    const targetUrl = resolveAccountNotificationUrl(n);
    if (targetUrl) navigate(targetUrl);
    else navigate("/account/notifications");
  };

  if (!isAuthenticated) return null;

  const togglePanel = () => {
    if (!open && items.length === 0) setLoading(true);
    setOpen((current) => !current);
  };

  return (
    <div className="relative z-[120]" ref={anchorRef}>
      {/* Bell button */}
      <button
        type="button"
        onClick={togglePanel}
        className="relative flex min-h-[40px] min-w-[40px] items-center justify-center rounded-lg p-2 transition hover:bg-neutral-100 dark:hover:bg-neutral-800"
        aria-label={count > 0 ? `${count} unread notifications` : "Notifications"}
        aria-haspopup="true"
        aria-expanded={open}
      >
        <Bell size={22} />
        {count > 0 && (
          <span className="absolute -top-1 -right-1 flex items-center justify-center min-w-[18px] h-[18px] rounded-full px-1 text-[10px] font-semibold bg-red-500 text-white">
            {count > 99 ? "99+" : count}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && createPortal(
        <div
          ref={panelRef}
          style={{ top: `${panelPos.top}px`, right: `${panelPos.right}px`, zIndex: 2000 }}
          className="theme-menu-panel fixed mt-0 w-[calc(100vw-1rem)] max-w-sm sm:w-96"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b px-4 py-3 theme-menu-divider">
            <span className="text-sm font-semibold text-neutral-900 dark:text-neutral-50">
              Notifications
            </span>
            {count > 0 && (
              <button
                type="button"
                onClick={handleMarkAllRead}
                disabled={markingAll}
                className="rounded-md text-[11px] font-semibold uppercase tracking-wider text-neutral-500 outline-none transition hover:text-neutral-800 focus-visible:ring-2 focus-visible:ring-neutral-400/70 dark:text-neutral-400 dark:hover:text-neutral-200 dark:focus-visible:ring-neutral-500/70 disabled:opacity-40"
              >
                {markingAll ? "Marking…" : "Mark all read"}
              </button>
            )}
          </div>

          {/* Items */}
          <div className="max-h-[min(360px,calc(100dvh-8rem))] overflow-y-auto">
            {loading && items.length === 0 ? (
              <div className="p-3"><ListSkeleton rows={3} label="Loading notifications" /></div>
            ) : items.length === 0 ? (
              <div className="theme-menu-empty py-10 text-center">
                <Bell size={24} className="mx-auto mb-2 text-neutral-300 dark:text-neutral-600" />
                <p>No notifications yet</p>
              </div>
            ) : (
              items.map((n) => {
                const cat = CAT_META[n.category] || CAT_META.SYSTEM;
                const { Icon, color, bg } = cat;
                return (
                  <div
                    key={n.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleItemClick(n)}
                    onKeyDown={(e) => e.key === "Enter" && handleItemClick(n)}
                    className={[
                      "theme-menu-item group cursor-pointer rounded-none border-b px-4 py-3 last:border-b-0 theme-menu-divider",
                      n.isRead
                        ? ""
                        : "theme-menu-item-active",
                    ].join(" ")}
                  >
                    <div className={`shrink-0 flex items-center justify-center h-8 w-8 rounded-full ${bg}`}>
                      <Icon size={14} className={color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={[
                          "text-[13px] font-medium leading-snug truncate",
                          n.isRead ? "theme-menu-item-subtle" : "text-neutral-900 dark:text-neutral-50",
                        ].join(" ")}>
                          {n.title}
                        </p>
                        <div className="shrink-0 flex items-center gap-1.5">
                          <span className="theme-menu-muted whitespace-nowrap text-[10px]">
                            {timeAgo(n.createdAt)}
                          </span>
                          {!n.isRead && (
                            <span className="h-1.5 w-1.5 rounded-full bg-blue-500 shrink-0" />
                          )}
                        </div>
                      </div>
                      <p className="theme-menu-muted mt-0.5 line-clamp-1 text-[11px]">
                        {n.body}
                      </p>
                    </div>
                    {/* Mark read on hover */}
                    {!n.isRead && (
                      <button
                        type="button"
                        onClick={(e) => handleMarkRead(e, n.id)}
                        className="theme-icon-button h-7 w-7 shrink-0 self-center p-0 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
                        title="Mark as read"
                      >
                        <Check size={12} className="theme-menu-item-icon" />
                      </button>
                    )}
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <Link
            to="/account/notifications"
            onClick={() => setOpen(false)}
            className="theme-menu-item theme-menu-item-subtle justify-center rounded-none border-t px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider theme-menu-divider"
          >
            See all notifications
          </Link>
        </div>,
        document.body
      )}
    </div>
  );
}
